import { SITE_NAME, SITE_URL, buildCanonical } from "./seo-config";

const LOGO_URL = `${SITE_URL}/favicon.png`;

export const organizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "EducationalOrganization",
  "@id": `${SITE_URL}#organization`,
  name: SITE_NAME,
  url: SITE_URL,
  logo: LOGO_URL,
  description:
    "Bullio Academy provides professional trading education with structured courses, live market analysis, and a community of active traders.",
  sameAs: [
    "https://twitter.com/BullioAcademy",
  ],
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}#website`,
  url: SITE_URL,
  name: SITE_NAME,
  publisher: { "@id": `${SITE_URL}#organization` },
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${SITE_URL}/insights?q={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
});

export const breadcrumbSchema = (
  items: Array<{ name: string; path: string }>,
) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, idx) => ({
    "@type": "ListItem",
    position: idx + 1,
    name: item.name,
    item: buildCanonical(item.path),
  })),
});

/** Seconds as an ISO 8601 duration, e.g. 10837 -> "PT3H0M37S". */
const isoDuration = (seconds: number) => {
  const s = Math.max(0, Math.round(seconds));
  return `PT${Math.floor(s / 3600)}H${Math.floor((s % 3600) / 60)}M${s % 60}S`;
};

interface CourseSchemaInput {
  name: string;
  description: string;
  path: string;
  image?: string;
  lessonsCount?: number;
  /** Total video runtime. Used for courseWorkload, which is a duration. */
  durationSeconds?: number;
}

export const courseSchema = ({
  name,
  description,
  path,
  image,
  lessonsCount,
  durationSeconds,
}: CourseSchemaInput) => ({
  "@context": "https://schema.org",
  "@type": "Course",
  name,
  description,
  url: buildCanonical(path),
  ...(image && { image }),
  provider: {
    "@type": "EducationalOrganization",
    name: SITE_NAME,
    sameAs: SITE_URL,
  },
  // courseWorkload is an ISO 8601 duration. This used to interpolate the
  // lesson count, publishing a 3-hour course to Google as "PT124H".
  ...((durationSeconds || lessonsCount) && {
    hasCourseInstance: {
      "@type": "CourseInstance",
      courseMode: "online",
      ...(durationSeconds ? { courseWorkload: isoDuration(durationSeconds) } : {}),
    },
  }),
  // The page that shows the price and the buy button. Not /enroll, which is a
  // free lead form and contradicts category "Paid"; and not the checkout
  // itself, which is another site and not a page search engines should send
  // people to cold.
  offers: {
    "@type": "Offer",
    category: "Paid",
    url: buildCanonical("/pricing"),
  },
});

interface ArticleSchemaInput {
  title: string;
  description: string;
  path: string;
  image?: string;
  authorName?: string;
  authorTitle?: string;
  publishedTime?: string;
  modifiedTime?: string;
  keywords?: string[];
  section?: string;
  wordCount?: number;
}

export const articleSchema = ({
  title,
  description,
  path,
  image,
  authorName,
  authorTitle,
  publishedTime,
  modifiedTime,
  keywords,
  section,
  wordCount,
}: ArticleSchemaInput) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: title,
  description,
  url: buildCanonical(path),
  image: image || `${SITE_URL}/og-image.webp`,
  ...(publishedTime && { datePublished: publishedTime }),
  ...(modifiedTime && { dateModified: modifiedTime }),
  ...(keywords && keywords.length > 0 && { keywords: keywords.join(", ") }),
  ...(section && { articleSection: section }),
  ...(wordCount && { wordCount }),
  // E-E-A-T: connect content to a named author with a stated role.
  author: {
    "@type": authorName && authorName !== SITE_NAME ? "Person" : "Organization",
    name: authorName ?? SITE_NAME,
    ...(authorTitle && { jobTitle: authorTitle }),
    url: SITE_URL,
  },
  publisher: {
    "@type": "Organization",
    name: SITE_NAME,
    logo: {
      "@type": "ImageObject",
      url: LOGO_URL,
    },
  },
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": buildCanonical(path),
  },
  isAccessibleForFree: true,
});

interface FaqItem {
  question: string;
  answer: string;
}

export const faqSchema = (items: FaqItem[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer,
    },
  })),
});

export const contactPageSchema = () => ({
  "@context": "https://schema.org",
  "@type": "ContactPage",
  name: `Contact ${SITE_NAME}`,
  url: buildCanonical("/contact"),
  about: { "@id": `${SITE_URL}#organization` },
});

export const aboutPageSchema = () => ({
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: `About ${SITE_NAME}`,
  url: buildCanonical("/about"),
  about: { "@id": `${SITE_URL}#organization` },
});
